import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { restaurantService } from '@/services/restaurantService';
import { createLogger } from '@/lib/logger';
import type { User, Session } from '@supabase/supabase-js';
import type { UserRole } from '@/types/auth.types';
import type { Database } from '@/types/database.types';

const log = createLogger('AuthService');

type UserRow = Database['public']['Tables']['users']['Row'];
type RestaurantRow = Database['public']['Tables']['restaurants']['Row'];

interface SignupData {
  email: string;
  password: string;
  restaurantName: string;
}

interface LoginData {
  email: string;
  password: string;
}

interface AuthResponse {
  success: boolean;
  error?: string;
  user?: User;
  session?: Session;
}

/**
 * Service d'authentification avec Supabase
 */
class AuthService {
  /**
   * Inscription d'un nouvel utilisateur et création du restaurant
   */
  async signup({ email, password, restaurantName }: SignupData): Promise<AuthResponse> {
    try {
      // 1. Créer l'utilisateur dans Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            restaurant_name: restaurantName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Aucun utilisateur créé');

      // 2. Créer le profil utilisateur EN PREMIER (sans restaurant_id pour l'instant)
      const { data: profile, error: profileError } = await supabase.from('users').insert({
          id: authData.user.id,
          email: authData.user.email!,
          display_name: restaurantName,
          role: 'admin' as const,
          restaurant_id: null,
          is_active: true,
        }).select().single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('Profil non créé');

      // 3. Créer le restaurant (maintenant le user existe dans la table users)
      const { data: restaurant, error: restaurantError } = await supabase.from('restaurants').insert({
          nom: restaurantName,
          owner_id: authData.user.id,
          plan: 'free',
        }).select().single();

      if (restaurantError) throw restaurantError;
      if (!restaurant) throw new Error('Restaurant non créé');

      // 4. Mettre à jour le profil avec le restaurant_id
      await supabase.from('users').update({
        restaurant_id: restaurant.id,
      }).eq('id', authData.user.id);

      // 5. Mettre à jour le store
      if (authData.session && profile) {
        useAuthStore.getState().login(authData.user, authData.session, {
          id: profile.id,
          email: profile.email,
          display_name: profile.display_name,
          role: profile.role as UserRole,
          restaurant_id: restaurant.id, // Utiliser restaurant.id car profile.restaurant_id était null
          is_active: profile.is_active,
          // Nouveau utilisateur = onboarding non complété
          onboarding_completed: false,
        });
      }

      return {
        success: true,
        user: authData.user,
        session: authData.session ?? undefined,
      };
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Connexion d'un utilisateur existant
   */
  async login({ email, password }: LoginData): Promise<AuthResponse> {
    try {
      // 0. D'abord, déconnecter toute session existante pour éviter les conflits
      const { data: existingSession } = await supabase.auth.getSession();
      if (existingSession?.session) {
        await supabase.auth.signOut();
      }

      // 1. Se connecter avec Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Aucun utilisateur trouvé');

      // 2. Récupérer le profil utilisateur
      let { data: profile, error: profileError } = await supabase.from('users').select('*').eq('id', authData.user.id).single();

      if (profileError) {
        // Si le profil n'existe pas, essayer de le créer
        if (profileError.code === 'PGRST116') {

          // Récupérer le restaurant de l'utilisateur
          const { data: restaurant } = await supabase.from('restaurants').select('*').eq('owner_id', authData.user.id).single();

          if (restaurant) {
            // Créer le profil
            const { data: newProfile, error: createError } = await supabase.from('users').insert({
              id: authData.user.id,
              email: authData.user.email!,
              display_name: authData.user.email!.split('@')[0], // Utiliser la partie avant @ de l'email
              role: 'admin' as const,
              restaurant_id: restaurant.id,
              is_active: true,
            }).select().single();

            if (createError) {
              throw createError;
            }

            profile = newProfile;
          } else {
            throw new Error('Restaurant non trouvé pour cet utilisateur');
          }
        } else {
          throw profileError;
        }
      }

      if (!profile) throw new Error('Profil utilisateur non trouvé');

      // 3. Vérifier que l'utilisateur est actif
      if (!profile.is_active) {
        throw new Error('Votre compte a été désactivé. Contactez un administratateur.');
      }

      // 4. Mettre à jour le store immédiatement
      useAuthStore.getState().login(authData.user, authData.session, {
        id: profile.id,
        email: profile.email,
        display_name: profile.display_name,
        role: profile.role as UserRole,
        restaurant_id: profile.restaurant_id,
        is_active: profile.is_active,
        onboarding_completed: profile.onboarding_completed ?? false,
        restaurant_type: profile.restaurant_type,
      });

      // 5. Charger les restaurants de l'utilisateur
      await this.loadUserRestaurants(authData.user.id);

      return {
        success: true,
        user: authData.user,
        session: authData.session,
      };
    } catch (error) {
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Déconnexion de l'utilisateur
   */
  async logout(): Promise<AuthResponse> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Nettoyer le store
      useAuthStore.getState().logout();

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer la session actuelle
   */
  async getSession(): Promise<Session | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    } catch (error) {
      console.error('Get session error:', error);
      return null;
    }
  }

  /**
   * Récupérer le profil utilisateur complet
   */
  async getUserProfile(userId: string) {
    try {
      const { data: profile, error } = await supabase.from('users').select('*, restaurants(*)').eq('id', userId).single();

      if (error) throw error;
      return profile;
    } catch (error) {
      console.error('Get user profile error:', error);
      return null;
    }
  }

  /**
   * Initialiser l'authentification au démarrage de l'app
   * Note: Cette fonction configure juste le listener, le traitement réel est dans setupAuthListener
   */
  async initialize() {
    useAuthStore.getState().setLoading(true);
    // Le listener va gérer INITIAL_SESSION et mettre loading à false
  }

  private listenerSetup = false;

  /**
   * Charger les restaurants de l'utilisateur et définir le restaurant actuel
   */
  async loadUserRestaurants(userId: string): Promise<void> {
    try {
      const restaurants = await restaurantService.getUserRestaurants(userId);
      useAuthStore.getState().setRestaurants(restaurants);

      if (restaurants.length > 0) {
        // Récupérer le restaurant sauvegardé ou utiliser le premier
        const savedId = restaurantService.getSelectedRestaurantId();
        const savedRestaurant = savedId
          ? restaurants.find((r) => r.id === savedId) ?? null
          : null;

        const currentRestaurant = savedRestaurant ?? restaurants[0] ?? null;
        useAuthStore.getState().setCurrentRestaurant(currentRestaurant);

        // Sauvegarder si pas déjà fait
        if (!savedId && currentRestaurant) {
          restaurantService.setSelectedRestaurantId(currentRestaurant.id);
        }
      }
    } catch {
      // Erreur silencieuse - les restaurants ne sont pas critiques
    }
  }

  /**
   * Écouter les changements d'état d'authentification
   */
  setupAuthListener() {
    // Éviter de configurer plusieurs listeners
    if (this.listenerSetup) {
      return;
    }
    this.listenerSetup = true;

    supabase.auth.onAuthStateChange(async (event, session) => {
      // Gérer INITIAL_SESSION et SIGNED_IN de la même manière
      if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session?.user) {

        // Timeout de 5 secondes pour éviter le blocage
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout: profile fetch took too long')), 5000)
        );

        try {
          const profilePromise = supabase.from('users').select('*').eq('id', session.user.id).single();

          const { data: profile } = await Promise.race([
            profilePromise,
            timeoutPromise
          ]) as { data: UserRow | null; error: unknown };

          if (profile) {
            useAuthStore.getState().login(session.user, session, {
              id: profile.id,
              email: profile.email,
              display_name: profile.display_name,
              role: profile.role as UserRole,
              restaurant_id: profile.restaurant_id,
              is_active: profile.is_active,
              onboarding_completed: profile.onboarding_completed ?? false,
              restaurant_type: profile.restaurant_type,
            });

            // Charger les restaurants avec timeout
            try {
              await Promise.race([
                this.loadUserRestaurants(session.user.id),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout: restaurants')), 5000))
              ]);
            } catch {
              // Timeout restaurants - on continue quand même
            }
          }
        } catch {
          // Erreur ou timeout - on continue
        } finally {
          // Toujours terminer le chargement
          useAuthStore.getState().setLoading(false);
        }
      } else if (event === 'INITIAL_SESSION' && !session) {
        // Pas de session - utilisateur non connecté
        useAuthStore.getState().logout();
        useAuthStore.getState().setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        useAuthStore.getState().logout();
      } else if (event === 'TOKEN_REFRESHED' && session) {
        useAuthStore.getState().setSession(session);
      }
    });
  }

  /**
   * Mettre à jour les paramètres utilisateur
   */
  async updateUserSettings(settings: {
    onboarding_completed?: boolean;
    restaurant_type?: string;
    display_name?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { user } = useAuthStore.getState();
      if (!user) {
        return { success: false, error: 'Utilisateur non connecté' };
      }

      const { error } = await supabase
        .from('users')
        .update(settings)
        .eq('id', user.id);

      if (error) throw error;

      // Mettre à jour le store local
      useAuthStore.getState().updateProfile(settings);

      return { success: true };
    } catch (error) {
      console.error('Update user settings error:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Formater les erreurs d'authentification
   */
  private formatError(error: unknown): string {
    if (error instanceof Error) {
      // Messages d'erreur Supabase Auth
      if (error.message.includes('Invalid login credentials')) {
        return 'Email ou mot de passe incorrect';
      }
      if (error.message.includes('Email not confirmed')) {
        return 'Veuillez confirmer votre email avant de vous connecter';
      }
      if (error.message.includes('User already registered')) {
        return 'Un compte existe déjà avec cet email';
      }
      if (error.message.includes('Failed to fetch')) {
        return 'Erreur de connexion au serveur. Vérifiez votre connexion internet.';
      }
      return error.message;
    }

    // Gérer les erreurs Supabase qui ont une structure différente
    if (error && typeof error === 'object') {
      const err = error as Record<string, unknown>;
      if (err.message && typeof err.message === 'string') {
        return err.message;
      }
      if (err.error_description && typeof err.error_description === 'string') {
        return err.error_description;
      }
      if (err.error && typeof err.error === 'string') {
        return err.error;
      }
    }

    return 'Une erreur inattendue est survenue. Consultez la console pour plus de détails.';
  }

  /**
   * Delete the current user's account and all associated data (RGPD)
   */
  async deleteAccount(confirmationText: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Non authentifié' };
      }

      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { confirmation: confirmationText },
      });

      if (error) {
        log.error('Account deletion failed', error);
        return { success: false, error: error.message || 'Erreur lors de la suppression' };
      }

      if (data?.error) {
        return { success: false, error: data.error };
      }

      // Clear local state
      await this.logout();

      return { success: true };
    } catch (err) {
      log.error('Account deletion error', err);
      return { success: false, error: this.getErrorMessage(err) };
    }
  }
}

// Export d'une instance unique
export const authService = new AuthService();
